import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// Translation resources
const resources = {
  en: {
    translation: {
      // Navigation
      nav: {
        dashboard: 'Dashboard',
        glucose: 'Blood Sugar',
        meals: 'Meals',
        medications: 'Medications',
        exercise: 'Exercise',
        recipes: 'Recipes',
        insights: 'AI Insights',
        shop: 'Shop',
        pricing: 'Pricing',
        profile: 'Profile',
        settings: 'Settings',
        logout: 'Logout',
        foodScanner: 'Food Scanner',
        community: 'Community',
        careTeam: 'Care Team',
        reports: 'Reports'
      },
      // Common
      common: {
        save: 'Save',
        cancel: 'Cancel',
        delete: 'Delete',
        edit: 'Edit',
        add: 'Add',
        search: 'Search',
        loading: 'Loading...',
        noData: 'No data available',
        success: 'Success',
        error: 'Error',
        confirm: 'Confirm',
        back: 'Back',
        next: 'Next',
        submit: 'Submit',
        close: 'Close'
      },
      // Dashboard
      dashboard: {
        title: 'Dashboard',
        welcome: 'Welcome back',
        todayStats: "Today's Statistics",
        glucoseLevel: 'Glucose Level',
        mealsLogged: 'Meals Logged',
        medicationsTaken: 'Medications Taken',
        exerciseMinutes: 'Exercise Minutes',
        recentActivity: 'Recent Activity',
        quickActions: 'Quick Actions'
      },
      // Glucose
      glucose: {
        title: 'Blood Sugar Log',
        addReading: 'Add Reading',
        level: 'Glucose Level',
        unit: 'mg/dL',
        time: 'Time',
        notes: 'Notes',
        beforeMeal: 'Before Meal',
        afterMeal: 'After Meal',
        fasting: 'Fasting',
        bedtime: 'Bedtime'
      },
      // Meals
      meals: {
        title: 'Meal Log',
        addMeal: 'Add Meal',
        mealType: 'Meal Type',
        breakfast: 'Breakfast',
        lunch: 'Lunch',
        dinner: 'Dinner',
        snack: 'Snack',
        carbs: 'Carbs',
        calories: 'Calories',
        protein: 'Protein',
        sugar: 'Sugar'
      },
      // Medications
      medications: {
        title: 'Medications',
        addMedication: 'Add Medication',
        name: 'Medication Name',
        dosage: 'Dosage',
        frequency: 'Frequency',
        time: 'Time',
        taken: 'Taken',
        missed: 'Missed',
        reminders: 'Reminders'
      },
      // Recipes
      recipes: {
        title: 'Recipes',
        search: 'Search recipes...',
        found: 'recipes found',
        category: 'Category',
        prepTime: 'Prep Time',
        difficulty: 'Difficulty',
        ingredients: 'Ingredients',
        instructions: 'Instructions',
        nutrition: 'Nutrition Facts'
      },
      // Food Scanner
      foodScanner: {
        title: 'Food Scanner',
        scanBarcode: 'Scan Barcode',
        enterBarcode: 'Enter barcode number',
        scan: 'Scan',
        searchFood: 'Search Food',
        results: 'Results',
        nutritionInfo: 'Nutrition Information',
        glycemicImpact: 'Glycemic Impact',
        highImpact: 'High Impact',
        mediumImpact: 'Medium Impact',
        lowImpact: 'Low Impact'
      },
      // Shop
      shop: {
        title: 'Diabetes Shop',
        subtitle: 'Amazon Best-Seller Products',
        viewOnAmazon: 'View on Amazon',
        addToWishlist: 'Add to Wishlist',
        allProducts: 'All Products',
        products: 'products',
        shopWithConfidence: 'Shop with Confidence'
      },
      // Pricing
      pricing: {
        title: 'Choose Your Plan',
        subtitle: 'Select the perfect plan for your diabetes management journey',
        currentPlan: 'Current Plan',
        free: 'Free',
        pro: 'Pro',
        enterprise: 'Enterprise',
        perMonth: '/month',
        subscribe: 'Subscribe Now',
        features: 'Features included',
        mostPopular: 'Most Popular'
      },
      // Auth
      auth: {
        login: 'Login',
        logout: 'Logout',
        signUp: 'Sign Up',
        email: 'Email',
        password: 'Password',
        forgotPassword: 'Forgot Password?',
        demoAccount: 'Try Demo Account',
        googleLogin: 'Continue with Google',
        orContinueWith: 'Or continue with'
      },
      // Profile
      profile: {
        title: 'Profile',
        personalInfo: 'Personal Information',
        diabetesInfo: 'Diabetes Information',
        preferences: 'Preferences',
        exportData: 'Export Data',
        exportPDF: 'Export as PDF',
        exportCSV: 'Export as CSV'
      },
      // Wearables
      wearables: {
        title: 'Connected Devices',
        connect: 'Connect Device',
        disconnect: 'Disconnect',
        googleFit: 'Google Fit',
        appleHealth: 'Apple Health',
        fitbit: 'Fitbit',
        lastSync: 'Last synced',
        syncNow: 'Sync Now'
      }
    }
  },
  fr: {
    translation: {
      nav: {
        dashboard: 'Tableau de bord',
        glucose: 'Glycémie',
        meals: 'Repas',
        medications: 'Médicaments',
        exercise: 'Exercice',
        recipes: 'Recettes',
        insights: 'Analyses IA',
        shop: 'Boutique',
        pricing: 'Tarifs',
        profile: 'Profil',
        settings: 'Paramètres',
        logout: 'Déconnexion',
        foodScanner: 'Scanner alimentaire',
        community: 'Communauté',
        careTeam: 'Équipe soignante',
        reports: 'Rapports'
      },
      common: {
        save: 'Enregistrer',
        cancel: 'Annuler',
        delete: 'Supprimer',
        edit: 'Modifier',
        add: 'Ajouter',
        search: 'Rechercher',
        loading: 'Chargement...',
        noData: 'Aucune donnée disponible',
        success: 'Succès',
        error: 'Erreur',
        confirm: 'Confirmer',
        back: 'Retour',
        next: 'Suivant',
        submit: 'Soumettre',
        close: 'Fermer'
      },
      dashboard: {
        title: 'Tableau de bord',
        welcome: 'Bienvenue',
        todayStats: "Statistiques du jour",
        glucoseLevel: 'Niveau de glucose',
        mealsLogged: 'Repas enregistrés',
        medicationsTaken: 'Médicaments pris',
        exerciseMinutes: 'Minutes d\'exercice',
        recentActivity: 'Activité récente',
        quickActions: 'Actions rapides'
      },
      glucose: {
        title: 'Journal de glycémie',
        addReading: 'Ajouter une mesure',
        level: 'Niveau de glucose',
        unit: 'mg/dL',
        time: 'Heure',
        notes: 'Notes',
        beforeMeal: 'Avant le repas',
        afterMeal: 'Après le repas',
        fasting: 'À jeun',
        bedtime: 'Au coucher'
      },
      meals: {
        title: 'Journal des repas',
        addMeal: 'Ajouter un repas',
        mealType: 'Type de repas',
        breakfast: 'Petit-déjeuner',
        lunch: 'Déjeuner',
        dinner: 'Dîner',
        snack: 'Collation',
        carbs: 'Glucides',
        calories: 'Calories',
        protein: 'Protéines',
        sugar: 'Sucre'
      },
      medications: {
        title: 'Médicaments',
        addMedication: 'Ajouter un médicament',
        name: 'Nom du médicament',
        dosage: 'Dosage',
        frequency: 'Fréquence',
        time: 'Heure',
        taken: 'Pris',
        missed: 'Manqué',
        reminders: 'Rappels'
      },
      recipes: {
        title: 'Recettes',
        search: 'Rechercher des recettes...',
        found: 'recettes trouvées',
        category: 'Catégorie',
        prepTime: 'Temps de préparation',
        difficulty: 'Difficulté',
        ingredients: 'Ingrédients',
        instructions: 'Instructions',
        nutrition: 'Informations nutritionnelles'
      },
      foodScanner: {
        title: 'Scanner alimentaire',
        scanBarcode: 'Scanner un code-barres',
        enterBarcode: 'Entrez le numéro du code-barres',
        scan: 'Scanner',
        searchFood: 'Rechercher un aliment',
        results: 'Résultats',
        nutritionInfo: 'Informations nutritionnelles',
        glycemicImpact: 'Impact glycémique',
        highImpact: 'Impact élevé',
        mediumImpact: 'Impact moyen',
        lowImpact: 'Impact faible'
      },
      shop: {
        title: 'Boutique Diabète',
        subtitle: 'Produits best-sellers Amazon',
        viewOnAmazon: 'Voir sur Amazon',
        addToWishlist: 'Ajouter à la liste',
        allProducts: 'Tous les produits',
        products: 'produits',
        shopWithConfidence: 'Achetez en toute confiance'
      },
      pricing: {
        title: 'Choisissez votre plan',
        subtitle: 'Sélectionnez le plan parfait pour gérer votre diabète',
        currentPlan: 'Plan actuel',
        free: 'Gratuit',
        pro: 'Pro',
        enterprise: 'Entreprise',
        perMonth: '/mois',
        subscribe: "S'abonner",
        features: 'Fonctionnalités incluses',
        mostPopular: 'Le plus populaire'
      },
      auth: {
        login: 'Connexion',
        logout: 'Déconnexion',
        signUp: "S'inscrire",
        email: 'Email',
        password: 'Mot de passe',
        forgotPassword: 'Mot de passe oublié?',
        demoAccount: 'Essayer le compte démo',
        googleLogin: 'Continuer avec Google',
        orContinueWith: 'Ou continuer avec'
      },
      profile: {
        title: 'Profil',
        personalInfo: 'Informations personnelles',
        diabetesInfo: 'Informations sur le diabète',
        preferences: 'Préférences',
        exportData: 'Exporter les données',
        exportPDF: 'Exporter en PDF',
        exportCSV: 'Exporter en CSV'
      },
      wearables: {
        title: 'Appareils connectés',
        connect: 'Connecter un appareil',
        disconnect: 'Déconnecter',
        googleFit: 'Google Fit',
        appleHealth: 'Apple Health',
        fitbit: 'Fitbit',
        lastSync: 'Dernière synchronisation',
        syncNow: 'Synchroniser'
      }
    }
  },
  es: {
    translation: {
      nav: {
        dashboard: 'Panel',
        glucose: 'Glucosa',
        meals: 'Comidas',
        medications: 'Medicamentos',
        exercise: 'Ejercicio',
        recipes: 'Recetas',
        insights: 'Análisis IA',
        shop: 'Tienda',
        pricing: 'Precios',
        profile: 'Perfil',
        settings: 'Configuración',
        logout: 'Cerrar sesión',
        foodScanner: 'Escáner de alimentos',
        community: 'Comunidad',
        careTeam: 'Equipo médico',
        reports: 'Informes'
      },
      common: {
        save: 'Guardar',
        cancel: 'Cancelar',
        delete: 'Eliminar',
        edit: 'Editar',
        add: 'Añadir',
        search: 'Buscar',
        loading: 'Cargando...',
        noData: 'Sin datos disponibles',
        success: 'Éxito',
        error: 'Error',
        confirm: 'Confirmar',
        back: 'Atrás',
        next: 'Siguiente',
        submit: 'Enviar',
        close: 'Cerrar'
      },
      dashboard: {
        title: 'Panel',
        welcome: 'Bienvenido',
        todayStats: 'Estadísticas de hoy',
        glucoseLevel: 'Nivel de glucosa',
        mealsLogged: 'Comidas registradas',
        medicationsTaken: 'Medicamentos tomados',
        exerciseMinutes: 'Minutos de ejercicio',
        recentActivity: 'Actividad reciente',
        quickActions: 'Acciones rápidas'
      },
      glucose: {
        title: 'Registro de glucosa',
        addReading: 'Añadir lectura',
        level: 'Nivel de glucosa',
        unit: 'mg/dL',
        time: 'Hora',
        notes: 'Notas',
        beforeMeal: 'Antes de comer',
        afterMeal: 'Después de comer',
        fasting: 'En ayunas',
        bedtime: 'Al acostarse'
      },
      meals: {
        title: 'Registro de comidas',
        addMeal: 'Añadir comida',
        mealType: 'Tipo de comida',
        breakfast: 'Desayuno',
        lunch: 'Almuerzo',
        dinner: 'Cena',
        snack: 'Merienda',
        carbs: 'Carbohidratos',
        calories: 'Calorías',
        protein: 'Proteínas',
        sugar: 'Azúcar'
      },
      auth: {
        login: 'Iniciar sesión',
        logout: 'Cerrar sesión',
        signUp: 'Registrarse',
        email: 'Correo',
        password: 'Contraseña',
        forgotPassword: '¿Olvidó su contraseña?',
        demoAccount: 'Probar cuenta demo',
        googleLogin: 'Continuar con Google',
        orContinueWith: 'O continuar con'
      },
      shop: {
        title: 'Tienda de Diabetes',
        subtitle: 'Productos más vendidos de Amazon',
        viewOnAmazon: 'Ver en Amazon',
        addToWishlist: 'Añadir a la lista',
        allProducts: 'Todos los productos',
        products: 'productos',
        shopWithConfidence: 'Compra con confianza'
      },
      pricing: {
        title: 'Elige tu plan',
        subtitle: 'Selecciona el plan perfecto para gestionar tu diabetes',
        currentPlan: 'Plan actual',
        free: 'Gratis',
        pro: 'Pro',
        enterprise: 'Empresa',
        perMonth: '/mes',
        subscribe: 'Suscribirse',
        features: 'Características incluidas',
        mostPopular: 'Más popular'
      }
    }
  },
  de: {
    translation: {
      nav: {
        dashboard: 'Dashboard',
        glucose: 'Blutzucker',
        meals: 'Mahlzeiten',
        medications: 'Medikamente',
        exercise: 'Bewegung',
        recipes: 'Rezepte',
        insights: 'KI-Einblicke',
        shop: 'Shop',
        pricing: 'Preise',
        profile: 'Profil',
        settings: 'Einstellungen',
        logout: 'Abmelden'
      },
      common: {
        save: 'Speichern',
        cancel: 'Abbrechen',
        delete: 'Löschen',
        edit: 'Bearbeiten',
        add: 'Hinzufügen',
        search: 'Suchen',
        loading: 'Laden...',
        noData: 'Keine Daten verfügbar'
      },
      auth: {
        login: 'Anmelden',
        logout: 'Abmelden',
        signUp: 'Registrieren',
        email: 'E-Mail',
        password: 'Passwort',
        demoAccount: 'Demo-Konto testen',
        googleLogin: 'Mit Google fortfahren',
        orContinueWith: 'Oder fortfahren mit'
      }
    }
  },
  pt: {
    translation: {
      nav: {
        dashboard: 'Painel',
        glucose: 'Glicose',
        meals: 'Refeições',
        medications: 'Medicamentos',
        exercise: 'Exercício',
        recipes: 'Receitas',
        insights: 'Análises IA',
        shop: 'Loja',
        pricing: 'Preços',
        profile: 'Perfil',
        logout: 'Sair'
      },
      common: {
        save: 'Salvar',
        cancel: 'Cancelar',
        delete: 'Excluir',
        edit: 'Editar',
        add: 'Adicionar',
        search: 'Pesquisar',
        loading: 'Carregando...'
      },
      auth: {
        login: 'Entrar',
        logout: 'Sair',
        signUp: 'Cadastrar',
        email: 'E-mail',
        password: 'Senha',
        demoAccount: 'Testar conta demo',
        googleLogin: 'Continuar com Google'
      }
    }
  }
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'en',
    debug: false,
    interpolation: {
      escapeValue: false
    },
    detection: {
      order: ['localStorage', 'navigator'],
      caches: ['localStorage']
    }
  });

export default i18n;
